// apps/client/src/pages/employees/components/EmployeeAllocationRow.tsx
import { Employee, ResearchActivity } from '@avanzu/types';
import { PersonAddAlt1, PersonRemoveAlt1Outlined } from '@mui/icons-material';
import {
  CircularProgress,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import EmployeeIcon from '../../../components/EmployeeIcon';
import { OptionalTooltip } from '../../../components/OptionalTooltip';
import PersonTickIcon from './PersonTickIcon';
import CustomPercentageInput from './CustomPercentageInput';

interface EmployeeAllocation {
  employeeId: string;
  projectAllocation: number;
  roleDescription: string;
  startDate: Date;
  endDate?: Date;
  activityAllocations: Record<string, number>;
  hasExistingAllocation?: boolean;
}

interface EmployeeAllocationRowProps {
  employee: Employee;
  allocation: EmployeeAllocation;
  activities: ResearchActivity[];
  saving: boolean;
  onProjectAllocationChange: (employeeId: string, value: string) => void;
  onRoleDescriptionChange: (employeeId: string, value: string) => void;
  onActivityAllocationChange: (employeeId: string, activityId: string, value: string) => void;
  onSaveAllocation: (employeeId: string) => void;
  onRemoveAllocation: (employeeId: string) => void;
}

export const EmployeeAllocationRow = React.memo<EmployeeAllocationRowProps>(
  ({
    employee,
    allocation,
    activities,
    saving,
    onProjectAllocationChange,
    onRoleDescriptionChange,
    onActivityAllocationChange,
    onSaveAllocation,
    onRemoveAllocation,
  }) => {
    // Local state for role description to prevent sluggish typing
    const [localRoleDescription, setLocalRoleDescription] = useState(
      allocation.roleDescription || ''
    );
    const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Update local state when allocation prop changes (for external updates)
    useEffect(() => {
      setLocalRoleDescription(allocation.roleDescription || '');
    }, [allocation.roleDescription]);

    // Custom debounce function using useRef
    const debouncedRoleUpdate = useCallback(
      (value: string) => {
        // Clear existing timeout
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }

        // Set new timeout
        debounceTimeoutRef.current = setTimeout(() => {
          onRoleDescriptionChange(employee.id, value);
        }, 300);
      },
      [employee.id, onRoleDescriptionChange]
    );

    // Cleanup timeout on unmount
    useEffect(() => {
      return () => {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
      };
    }, []);

    // Memoized calculations
    const totalAllocated = React.useMemo(() => {
      return Object.values(allocation.activityAllocations).reduce((sum, val) => sum + val, 0);
    }, [allocation.activityAllocations]);

    const isComplete = React.useMemo(() => {
      if (allocation.projectAllocation === 0) return true; // 0% is valid (not allocated to project)
      return Math.abs(totalAllocated - allocation.projectAllocation) < 0.01; // Activity allocations should equal project allocation
    }, [allocation.projectAllocation, totalAllocated]);

    const canAllocateActivities = React.useMemo(() => {
      return allocation.projectAllocation > 0;
    }, [allocation.projectAllocation]);

    const canSaveAllocation = React.useMemo(() => {
      return allocation.projectAllocation > 0 && localRoleDescription.trim().length > 0;
    }, [allocation.projectAllocation, localRoleDescription]);

    const canRemoveAllocation = React.useMemo(() => {
      return allocation.projectAllocation > 0;
    }, [allocation.projectAllocation]);

    // Memoized event handlers
    const handleProjectAllocationChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        onProjectAllocationChange(employee.id, e.target.value);
      },
      [employee.id, onProjectAllocationChange]
    );

    const handleRoleDescriptionChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLocalRoleDescription(value); // Immediate local update
        debouncedRoleUpdate(value); // Debounced parent update
      },
      [debouncedRoleUpdate]
    );

    const handleActivityAllocationChange = useCallback(
      (activityId: string, value: string) => {
        return onActivityAllocationChange(employee.id, activityId, value);
      },
      [employee.id, onActivityAllocationChange]
    );

    const handleSaveAllocation = useCallback(() => {
      onSaveAllocation(employee.id);
    }, [employee.id, onSaveAllocation]);

    const handleRemoveAllocation = useCallback(() => {
      onRemoveAllocation(employee.id);
    }, [employee.id, onRemoveAllocation]);

    // Employment range — "Jan 2022 – Mar 2025" or "Jan 2022 – Current".
    // Commented out per request — the "Employed during FY" filter already
    // communicates the relevant scoping; leaving the helper here for easy
    // re-enable if we want per-row dates back later.
    // const employmentRange = React.useMemo(() => {
    //   const fmt = (d: Date | string | undefined) => {
    //     if (!d) return null;
    //     const date = d instanceof Date ? d : new Date(d);
    //     if (Number.isNaN(date.getTime())) return null;
    //     return date.toLocaleDateString('en-AU', { month: 'short', year: 'numeric' });
    //   };
    //   const start = fmt(employee.startDate);
    //   const end = fmt(employee.endDate);
    //   if (!start) return null;
    //   return `${start} – ${end ?? 'Current'}`;
    // }, [employee.startDate, employee.endDate]);

    return (
      <TableRow>
        {/* Employee Details */}
        <TableCell>
          <Stack>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
              <EmployeeIcon type={employee.type} isActive={employee.isActive} />{' '}
              {employee.firstName} {employee.lastName}{' '}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {employee.department}
            </Typography>
            {/* {employmentRange && (
              <Typography variant="caption" color="text.disabled">
                {employmentRange}
              </Typography>
            )} */}
          </Stack>
          <Stack>
            <TextField
              size="small"
              label="Describe research role"
              variant="outlined"
              value={localRoleDescription}
              onChange={handleRoleDescriptionChange}
              sx={{
                mt: 1,
                '& .MuiInputBase-input': {
                  fontSize: '0.75rem',
                  padding: 0.5,
                },
                '& .MuiOutlinedInput-root': {
                  padding: 0.5,
                },
              }}
              multiline
              rows={2}
              required
              error={allocation.projectAllocation > 0 && !localRoleDescription.trim()}
              helperText={
                allocation.projectAllocation > 0 && !localRoleDescription.trim()
                  ? 'Role description required for allocated employees'
                  : ''
              }
            />
          </Stack>
        </TableCell>

        {/* Actions */}
        <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle', padding: 0, pt: 5 }}>
          <OptionalTooltip
            title="Remove employee allocation"
            disabled={!canRemoveAllocation || saving || !allocation.hasExistingAllocation}
          >
            <IconButton
              size="small"
              disabled={!canRemoveAllocation || saving || !allocation.hasExistingAllocation}
              color="error"
              onClick={handleRemoveAllocation}
            >
              {saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PersonRemoveAlt1Outlined />
              )}
            </IconButton>
          </OptionalTooltip>
          <OptionalTooltip
            title={`${allocation.hasExistingAllocation ? 'Update' : 'Create'} employee allocation`}
            disabled={!canSaveAllocation || saving}
          >
            <IconButton
              size="small"
              disabled={!canSaveAllocation || saving}
              color="success"
              onClick={handleSaveAllocation}
            >
              {saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : allocation.hasExistingAllocation ? (
                <PersonTickIcon />
              ) : (
                <PersonAddAlt1 />
              )}
            </IconButton>
          </OptionalTooltip>
        </TableCell>

        {/* Project Allocation */}
        <TableCell
          sx={{
            width: 60,
            minWidth: 60,
            borderBottom: '0px none',
            backgroundColor: '#EFF2FE',
            textAlign: 'center',
            verticalAlign: 'middle',
            padding: 0,
            pt: 5,
          }}
        >
          <TextField
            size="small"
            value={allocation.projectAllocation || 0}
            onChange={handleProjectAllocationChange}
            InputProps={{
              endAdornment: <Typography variant="caption">%</Typography>,
            }}
            inputProps={{
              min: 0,
              max: 100,
              step: 1,
              style: { textAlign: 'center', fontWeight: 600 },
            }}
            variant="outlined"
            sx={{
              width: '80px',
              '& .MuiOutlinedInput-input': {
                fontSize: '0.75rem',
                fontWeight: 600,
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'white',
              },
            }}
          />
        </TableCell>

        {/* Total Percentage */}
        <TableCell
          sx={{
            width: 60,
            minWidth: 60,
            textAlign: 'center',
            verticalAlign: 'middle',
            fontWeight: 600,
            padding: 0,
            pt: 5,
          }}
        >
          <Typography
            variant="caption"
            color={
              !canAllocateActivities
                ? 'text.disabled'
                : totalAllocated === 100
                  ? 'success.main'
                  : totalAllocated > 100
                    ? 'error'
                    : 'text.secondary'
            }
            fontWeight={500}
          >
            {canAllocateActivities ? `${totalAllocated.toFixed(0)}%` : '-'}
          </Typography>
        </TableCell>

        {/* Activity Allocations */}
        {activities.map((activity) => (
          <TableCell
            key={activity.id}
            sx={{ width: 80, minWidth: 80, p: 1, pb: 0, pt: 5, textAlign: 'center' }}
          >
            <CustomPercentageInput
              value={allocation.activityAllocations[activity.id] || 0}
              onChange={(value) => handleActivityAllocationChange(activity.id, value)}
              disabled={!canAllocateActivities}
              width={80}
            />
          </TableCell>
        ))}
      </TableRow>
    );
  }
);

EmployeeAllocationRow.displayName = 'EmployeeAllocationRow';
